import { getUserTenantId } from "@talentech/bff-login";
import { Editor, ProjectData } from "grapesjs";
import { useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { PageTypes } from "../../../App";
import {
  getFaviconValues,
  getOpenGraphValues,
  OpenGraphHeadHTML,
} from "../../../shared/openGraph";
import {
  useGetPagesQuery,
  Template,
  useLazyGetPagesQuery,
} from "../../../redux/reducer/pages.api";
import { service } from "../../../service/service";
import { createRemoteStorage } from "./remoteStorage";
import {
  consentCoreHTML,
  cookiebotHeadHTML,
  findPageDomain,
  isEmbeddedInIframe,
  ontameHeadHTML,
  pageBodyHTML,
} from "./pageDocument";
import { CustomCommands, getExternalResources } from "@/shared/utils";
import { customersApi, useGetCustomerConfigQuery } from "@/redux/reducer/customers.api";
import { getLanguageCode } from "@/shared/i18n_utils";
import type { AppDispatch } from "@/redux/store";
import { configAtSave } from "./frameSetting";

export function useStoreManager() {
  const { id, type } = useParams<{ id: string; type: PageTypes }>();
  const [getPages] = useLazyGetPagesQuery();
  // Canonical tenant id selector (matches EditorWrapper) so the customer-config
  // query shares one RTK cache key instead of risking a duplicate round-trip.
  const tenantId = useSelector(getUserTenantId);
  const { data: customerConfig } = useGetCustomerConfigQuery({
    tenantId: tenantId as string,
  });
  const customerConfigRef = useRef(customerConfig);
  customerConfigRef.current = customerConfig;
  const dispatch = useDispatch<AppDispatch>();
  // The iframe switch can change in another tab while the editor is open, so every save reads it again.
  const fetchCustomerConfig = async () => {
    const request = dispatch(
      customersApi.endpoints.getCustomerConfig.initiate(
        { tenantId: tenantId as string },
        { forceRefetch: true }
      )
    );
    try {
      return await request.unwrap();
    } finally {
      request.unsubscribe();
    }
  };


  const { currentTemplate } = useGetPagesQuery(
    {},
    {
      selectFromResult: ({ data, ...rest }) => ({
        // @ts-ignore
        currentTemplate: (data?.[`${type}_templates`] || []).find(
          (template: Template) => template.id === id
        ) as Template,
        ...rest,
      }),
    }
  );
  // GrapesJS registers the storage once; store()/load() read the template via
  // this ref, not via the closure of the render that registered it.
  const currentTemplateRef = useRef<Template | undefined>(currentTemplate);
  currentTemplateRef.current = currentTemplate;

  const onStore = async (data: ProjectData, editor: Editor) => {
    const customerConfig = await configAtSave({
      fetchFresh: fetchCustomerConfig,
      lastKnown: customerConfigRef.current,
    });
    const templateId = currentTemplateRef.current?.id as string;
    const pageDomain = findPageDomain(customerConfig.domains, templateId);
    const embeddedInIframe = isEmbeddedInIframe(customerConfig.domains, templateId);
    const languageCode = getLanguageCode(pageDomain?.language || "en");
    const formData = new FormData();

    const externalJS = getExternalResources(data)
      .javascript.map((url) => `<script src="${url}"></script>`)
      .join("");

    const externalCSS = getExternalResources(data)
      .css.map((url) => `<link rel="stylesheet" href="${url}" />`)
      .join("");

    const htmlSrc = editor.getHtml();
    const cssSrc = editor.getCss({
      keepUnusedStyles: true,
    });

    const jsSrc = editor.DataSources.get("custom-js")
      ?.getRecord("raw-js")
      ?.values()[2];


    const customFonts = editor.runCommand(CustomCommands.GET_FONT_LINKS) as string[];

    const faviconHTML = getFaviconValues(editor);
    const openGraphMeta = getOpenGraphValues(editor);
    const headHTML = [
      '<meta charset="UTF-8">',
      '<meta name="viewport" content="width=device-width, initial-scale=1.0" />',
      consentCoreHTML(embeddedInIframe),
      cookiebotHeadHTML({ settings: customerConfig.settings, embeddedInIframe }),
      customFonts.map((font) => `<link rel="stylesheet" href="${font}" />`).join(""),
      externalCSS,
      externalJS,
      `<style>${cssSrc}</style>`,
    ];

    if (faviconHTML) {
      headHTML.push(
        `<link rel="icon" href="${getFaviconValues(editor).link}" />`
      );
    }
    if (openGraphMeta) {
      headHTML.push(OpenGraphHeadHTML(openGraphMeta));
    }

    headHTML.push(
      ...ontameHeadHTML({
        settings: customerConfig.settings,
        tenantId: tenantId as string,
        isJobPage: type === PageTypes.JOB,
        embeddedInIframe,
      })
    );

    const bodyHTML = pageBodyHTML({ htmlSrc, jsSrc });

    const htmlDocument = `<html lang="${languageCode}"><head>
    ${headHTML.join("")}    
    </head>
    ${bodyHTML}
    </html>`;

    const htmlDocumentBlob = new Blob([htmlDocument], { type: "text/html" });
    const jsonDataBlob = new Blob([JSON.stringify(data)], {
      type: "application/json",
    });

    formData.append("files", htmlDocumentBlob, `index.html`);
    formData.append(`files`, jsonDataBlob, `index.json`);
    return formData;
  };

  const remoteStorageOpts = (editor: Editor) => {
    editor.Storage.add(
      "remote",
      createRemoteStorage<Template>({
        type: type as PageTypes,
        getTemplate: () => currentTemplateRef.current,
        loadTemplate: () =>
          getPages({})
            .unwrap()
            .then((res) => {
              const templates =
                type === PageTypes.PAGE
                  ? res.page_templates
                  : res.job_templates;
              return templates.find((template: Template) => template.id === id);
            }),
        fetchTemplate: service.fetchTemplate,
        saveTemplate: service.saveTemplate,
      })
    );
  };

  return {
    onStore,
    remoteStorageOpts,
  };
}
